// lib/client/api/cart.ts
import { CartResponse } from "@/lib/types/cart"

/**
 * Fetches the user's cart details from the client-side.
 * Calls the GET /api/cart route handler.
 */
export const getCartClient = async (): Promise<CartResponse> => {
  const res = await fetch("/api/cart", {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({})) // Try to parse error
    throw new Error(
      errorData.message || `Failed to fetch cart: ${res.statusText}`
    )
  }

  return res.json()
}

/**
 * Updates the quantity of a specific item in the cart from the client-side.
 * Calls the PUT /api/cart/[itemId] route handler.
 * @param itemId - The unique ID of the cart item instance to update.
 * @param quantity - The new quantity for the item.
 */
export const updateCartItemClient = async (
  itemId: string | number,
  quantity: number
): Promise<CartResponse> => {
  const res = await fetch(`/api/cart/${itemId}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ quantity }),
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(
      errorData.message || `Failed to update cart item: ${res.statusText}`
    )
  }

  return res.json()
}

/**
 * Removes a specific item from the cart from the client-side.
 * Calls the DELETE /api/cart/[itemId] route handler.
 * @param itemId - The unique ID of the cart item instance to remove.
 */
export const removeCartItemClient = async (
  itemId: string | number
): Promise<CartResponse> => {
  const res = await fetch(`/api/cart/${itemId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    throw new Error(
      errorData.message || `Failed to remove cart item: ${res.statusText}`
    )
  }

  return res.json()
}
